import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

import { create as createSchema } from "@blis/shared/schemas/material";
import { trpc } from "@blis/trpc/react";

import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import type { MaterialSpecDetail } from "../../types";
import { AdminForm } from "../AdminForm";
import { TabLayout } from "../TabLayout";

const MATERIAL_TYPE_OPTIONS = [
  { value: "MEDIA", label: "Media" },
  { value: "REAGENT", label: "Reagent" },
  { value: "AGAR", label: "Agar" },
  { value: "OTHER", label: "Other" },
] as const;

type FormValues = z.infer<typeof createSchema>;

export function MaterialDetailForm({
  material,
}: {
  material?: MaterialSpecDetail;
}) {
  const navigate = useNavigate();
  const create = trpc.admin.materialSpec.create.useMutation(
    mutationResponse({
      errorTitle: "Error adding material",
      successMessage: "Material added",
      onSuccess: (data) => {
        navigate({
          to: "/admin/materials/$id/edit",
          params: { id: data.id },
          replace: true,
        });
      },
    }),
  );
  const update = trpc.admin.materialSpec.update.useMutation(
    mutationResponse({
      errorTitle: "Error updating material",
      successMessage: "Material updated",
    }),
  );

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      name: material?.name || "",
      type: material?.type || "MEDIA",
      code: material?.code || "",
      lotLabel: material?.lotLabel || "Lot #",
    },
    resolver: zodResolver(createSchema),
  });

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (material) {
      update.mutate({ id: material.id, ...values });
    } else {
      create.mutate(values);
    }
  };

  return (
    <TabLayout>
      <AdminForm onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                isInvalid={!!error}
                validationBehavior="aria"
                className="col-span-1 md:col-span-2"
              >
                <Label>Name</Label>
                <Input />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />

          <Controller
            control={control}
            name="type"
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                selectedKey={field.value}
                onSelectionChange={(value) => field.onChange(value)}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Type</Label>
                <SelectTrigger />
                <SelectList>
                  {MATERIAL_TYPE_OPTIONS.map((option) => (
                    <SelectItem
                      key={option.value}
                      id={option.value}
                      textValue={option.label}
                    >
                      <Text slot="label">{option.label}</Text>
                    </SelectItem>
                  ))}
                </SelectList>
                <FieldError>{error?.message}</FieldError>
              </Select>
            )}
          />

          <Controller
            control={control}
            name="code"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Code</Label>
                <Input />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />

          <Controller
            control={control}
            name="lotLabel"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                isInvalid={!!error}
                validationBehavior="aria"
                className="col-span-1 md:col-span-2"
              >
                <Label>Lot Label</Label>
                <Input />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
        </div>

        <Separator />

        <div className="flex justify-end gap-2">
          <Link to="/admin/materials" variant="outline" asButton>
            Cancel
          </Link>
          <Button
            type="submit"
            isPending={create.isPending || update.isPending}
            isDisabled={create.isPending || update.isPending}
          >
            Save
          </Button>
        </div>
      </AdminForm>
    </TabLayout>
  );
}
